/* Native media only: no decoding, processing, waveform synthesis or audio autoplay. */
if (!customElements.get('rw-audio-player')) {
  class RussWaveAudio extends HTMLElement {
    static active = null;
    connectedCallback() {
      if (this.events && !this.events.signal.aborted) return;
      this.events = new AbortController();
      const { signal } = this.events;
      this.audio = this.querySelector('audio');
      this.button = this.querySelector('[data-audio-toggle]');
      this.seek = this.querySelector('[data-audio-seek]');
      this.status = this.querySelector('[data-audio-status]');
      this.time = this.querySelector('[data-audio-time]');
      this.duration = this.querySelector('[data-audio-duration]');
      if (!this.audio || !this.button || !this.seek) return;
      this.button.disabled = !this.audio.getAttribute('src');
      const toggle = () => this.wantsPlay ? this.stop() : this.start();
      this.button.addEventListener('click', toggle, { signal });
      this.querySelector('[data-audio-title]')?.addEventListener('click', toggle, { signal });
      this.addEventListener('shopify:block:select', () => {
        this.selectTrack();
        this.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }, { signal });
      const list = this.closest('.rw-playlist');
      if (list && !list.querySelector('[data-selected="true"]')) this.selectTrack();
      this.seek.addEventListener('input', () => {
        if (!Number.isFinite(this.audio.duration)) return;
        this.audio.currentTime = Math.min(this.audio.duration, Math.max(0, Number(this.seek.value)));
        this.updateTime();
      }, { signal });
      this.querySelectorAll('[data-audio-source]').forEach(button => {
        button.addEventListener('click', () => this.switchSource(button), { signal });
      });
      this.audio.addEventListener('loadedmetadata', () => {
        if (Number.isFinite(this.pendingPosition) && Number.isFinite(this.audio.duration) && this.audio.duration > 0) {
          this.audio.currentTime = Math.min(this.pendingPosition, Math.max(0, this.audio.duration - .01));
          this.pendingPosition = null;
        }
        this.updateTime();
      }, { signal });
      this.audio.addEventListener('durationchange', () => this.updateTime(), { signal });
      this.audio.addEventListener('timeupdate', () => this.updateTime(), { signal });
      this.audio.addEventListener('playing', () => {
        if (!this.wantsPlay || RussWaveAudio.active !== this) { this.audio.pause(); return; }
        this.dataset.state = 'playing';
        this.status.textContent = '';
        this.updateButton();
      }, { signal });
      this.audio.addEventListener('waiting', () => {
        if (this.wantsPlay) { this.dataset.state = 'loading'; this.status.textContent = this.dataset.loading; }
      }, { signal });
      this.audio.addEventListener('pause', () => {
        // A stale pause from replacing the A/B source must not cancel the new explicit request.
        if (!this.audio.paused || this.switching || this.dataset.state === 'error') return;
        this.wantsPlay = false;
        this.dataset.state = 'paused';
        this.status.textContent = '';
        this.updateButton();
      }, { signal });
      this.audio.addEventListener('ended', () => { this.stop(); this.audio.currentTime = 0; this.updateTime(); }, { signal });
      this.audio.addEventListener('error', () => this.fail(), { signal });
      this.updateButton();
      this.updateTime();
    }
    disconnectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) return;
        this.stop();
        this.events?.abort();
        if (RussWaveAudio.active === this) RussWaveAudio.active = null;
        // Retain the URL for Theme Editor reinsertion, while releasing network/decoder resources.
        if (this.audio) { const src = this.audio.getAttribute('src'); this.audio.removeAttribute('src'); this.audio.load(); if (src) this.audio.setAttribute('src', src); }
      });
    }
    selectTrack() {
      this.closest('.rw-playlist')?.querySelectorAll('rw-audio-player').forEach(player => {
        player.dataset.selected = String(player === this);
      });
    }
    async start() {
      if (!this.audio?.getAttribute('src')) return;
      this.selectTrack();
      if (RussWaveAudio.active && RussWaveAudio.active !== this) RussWaveAudio.active.stop();
      RussWaveAudio.active = this;
      this.wantsPlay = true;
      const token = this.request = (this.request || 0) + 1;
      const connection = this.events;
      if (this.dataset.state === 'error') this.audio.load();
      this.dataset.state = 'loading';
      this.status.textContent = this.dataset.loading;
      this.updateButton();
      try {
        await this.audio.play();
        if (token !== this.request || connection.signal.aborted || !this.wantsPlay || RussWaveAudio.active !== this) {
          if (RussWaveAudio.active !== this || !this.wantsPlay) this.audio.pause();
        }
      } catch (error) {
        if (token === this.request && !connection.signal.aborted && this.wantsPlay) this.fail();
      }
    }
    stop() {
      this.request = (this.request || 0) + 1;
      this.wantsPlay = false;
      this.audio?.pause();
      this.dataset.state = 'paused';
      if (this.status) this.status.textContent = '';
      this.updateButton();
    }
    fail() {
      this.stop();
      if (RussWaveAudio.active === this) RussWaveAudio.active = null;
      this.dataset.state = 'error';
      if (this.status) this.status.textContent = this.dataset.error;
    }
    switchSource(button) {
      if (button.getAttribute('aria-pressed') === 'true') return;
      const resume = this.wantsPlay;
      const position = Number.isFinite(this.pendingPosition) ? this.pendingPosition : this.audio.currentTime || 0;
      this.selectTrack();
      this.stop();
      this.switching = true;
      this.pendingPosition = position;
      this.audio.src = button.dataset.audioSource;
      this.audio.load();
      this.querySelectorAll('[data-audio-source]').forEach(choice => choice.setAttribute('aria-pressed', String(choice === button)));
      this.updateTime();
      if (resume) this.start();
      queueMicrotask(() => { this.switching = false; });
    }
    updateButton() {
      if (!this.button) return;
      this.button.setAttribute('aria-label', this.wantsPlay ? this.dataset.pause : this.dataset.play);
      this.button.setAttribute('aria-pressed', String(!!this.wantsPlay));
      this.querySelector('[data-audio-title]')?.setAttribute('aria-label', this.wantsPlay ? this.dataset.pause : this.dataset.play);
    }
    format(seconds) {
      if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
      return Math.floor(seconds / 60) + ':' + String(Math.floor(seconds % 60)).padStart(2, '0');
    }
    updateTime() {
      if (!this.audio || !this.seek) return;
      const duration = this.audio.duration;
      const ready = Number.isFinite(duration) && duration > 0;
      this.seek.disabled = !ready;
      this.seek.max = ready ? duration : 1;
      this.seek.value = ready ? this.audio.currentTime : 0;
      this.seek.setAttribute('aria-valuetext', this.format(this.audio.currentTime) + ' / ' + (ready ? this.format(duration) : '—'));
      this.time.textContent = this.format(this.audio.currentTime);
      this.duration.textContent = ready ? this.format(duration) : '—';
      this.seek.style.setProperty('--rw-progress', ready ? (this.audio.currentTime / duration * 100) + '%' : '0%');
    }
  }
  customElements.define('rw-audio-player', RussWaveAudio);
}
